import { useTaxonsGetTaxon } from '@/api/TaxonomyApi/generated/taxons/taxons';
import { List } from '@/components/List/List';
import { ScreenHeader } from '@/components/ScreenHeader';
import { TabScreenContainer } from '@/components/TabScreenContainer';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useSpeciesSelectionStore } from '@/store/speciesSelection/speciesSelection.store';
import { Button } from '@components/Button/Button';
import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter, useSegments } from 'expo-router';
import { useCallback, useEffect, useState } from 'react';
import {
  ScrollView,
  Separator,
  SizableText,
  Spinner,
  XStack,
  YStack,
  useTheme,
} from 'tamagui';
import { SpeciesSearchBackAction } from '../components/SpeciesSearchBackAction';
import {
  navigateBackToSpeciesForm,
  navigateBackToSpeciesSearch,
} from '../speciesSearch.navigation';
import {
  GENUS_RANK_CODE,
  SPECIES_RANK_CODE,
  SUBMITTABLE_RANK_CODES,
  TAXONOMY_ROOT_TAXON_CODE,
  taxonomyLevelKeys,
} from './speciesTaxonomy.constants';
import {
  SpeciesTaxonomyQueryParams,
  TaxonomyListItem,
} from './speciesTaxonomy.types';
import {
  buildTaxonItem,
  buildTaxonomyItems,
  buildTaxonomySections,
  getLastAssignedTaxon,
  isUnsupportedLeafTaxonomySelection,
  resolveRankLabel,
} from './speciesTaxonomy.utils';
import { TaxonomyBadge } from './SpeciesTaxonomyScreen.styled';

const isSubmittableRank = (rankCode?: string): boolean =>
  Boolean(rankCode && SUBMITTABLE_RANK_CODES.includes(rankCode.toLowerCase()));

export const SpeciesTaxonomyScreen = (): React.ReactElement => {
  const { t, language } = useAppTranslation();
  const { isSmallScreen } = useBreakpoint();
  const theme = useTheme();
  const router = useRouter();
  const segments = useSegments() as string[];
  const { reportId, rootTaxon } =
    useLocalSearchParams<SpeciesTaxonomyQueryParams>();
  const { setSelection } = useSpeciesSelectionStore();
  const [selectedPath, setSelectedPath] = useState<TaxonomyListItem[]>([]);
  const currentLevelIndex = selectedPath.length;
  const lastSelection = selectedPath.at(-1);
  const lastSelectionRankCode = lastSelection?.rankCode?.toLowerCase();
  const { assignedTaxon: activeItem, unassignedOffset } =
    getLastAssignedTaxon(selectedPath);

  const activeTaxonCode =
    activeItem?.id ?? rootTaxon ?? TAXONOMY_ROOT_TAXON_CODE;

  const {
    data: currentTaxonResponse,
    isFetching,
    isLoading,
  } = useTaxonsGetTaxon(activeTaxonCode, {
    query: {
      staleTime: 30_000,
    },
  });

  const rootTaxonItem =
    currentTaxonResponse && currentTaxonResponse.code === rootTaxon
      ? buildTaxonItem(
          currentTaxonResponse,
          currentTaxonResponse.rank.code,
          language,
        )
      : undefined;
  const availableRanks = currentTaxonResponse?.childrenByRank ?? [];
  const orderedRanks = [...availableRanks].sort(
    (first, second) => first.order - second.order,
  );
  const currentRank = orderedRanks[unassignedOffset];
  const taxonomyItemsList = buildTaxonomyItems({
    language,
    rank: currentRank,
    hasNextRank: unassignedOffset < orderedRanks.length - 1,
    activeSelectionId: activeTaxonCode,
    unassignedLabel: t('speciesSearch.taxonomy.unassigned'),
  });
  const taxonomyRankLabel =
    resolveRankLabel(language, currentRank?.names) ?? currentRank?.code;
  const rootBadgeLabel = t('speciesSearch.taxonomy.select', {
    level: selectedPath[0]?.rankCode?.toLowerCase() ?? taxonomyLevelKeys[1],
  });

  const canShowManualValidation = lastSelectionRankCode === GENUS_RANK_CODE;
  const isUnsupportedLeafSelection = isUnsupportedLeafTaxonomySelection({
    selection: lastSelection,
    taxonResponse: currentTaxonResponse,
    rootTaxonCode: TAXONOMY_ROOT_TAXON_CODE,
    isSubmittableRank,
  });

  const handleSelectTaxonomy = (item: TaxonomyListItem): void => {
    if (item.isUnassigned) {
      setSelectedPath((previous) => {
        return [...previous.slice(0, currentLevelIndex), item];
      });
      return;
    }

    if (!item.taxonCode) {
      return;
    }

    setSelectedPath((previous) => {
      return [...previous.slice(0, currentLevelIndex), item];
    });
  };

  const sections = buildTaxonomySections(
    taxonomyItemsList,
    theme.color?.val,
    handleSelectTaxonomy,
  );

  const handleRemoveSelection = (index: number): void => {
    setSelectedPath((previous) => previous.slice(0, index + 1));
  };

  const handleValidate = useCallback((): void => {
    const lastSelection = selectedPath[selectedPath.length - 1];
    if (!lastSelection || !lastSelection.taxonCode) {
      return;
    }
    setSelection({
      value: lastSelection.scientificDisplayName ?? lastSelection.taxonCode,
      label: lastSelection.label,
    });
    navigateBackToSpeciesForm({
      router,
      segments,
      reportId,
    });
  }, [reportId, router, segments, selectedPath, setSelection]);

  const handleTrySearch = (): void => {
    navigateBackToSpeciesSearch(router);
  };

  const handleStopIdentification = (): void => {
    handleValidate();
  };

  useEffect(() => {
    if (rootTaxonItem) {
      setSelectedPath([rootTaxonItem]);
    }
  }, [rootTaxonItem]);

  useEffect(() => {
    const currentRank = activeItem?.rankCode?.toLowerCase();
    const isCurrentTaxonLoaded = currentTaxonResponse?.code === activeTaxonCode;
    if (
      currentRank === SPECIES_RANK_CODE || // Rank is species
      (currentRank === GENUS_RANK_CODE &&
        isCurrentTaxonLoaded &&
        sections.length === 0 &&
        !isFetching) // OR Rank is a genus with no children
    ) {
      handleValidate();
    }
  }, [
    activeItem?.rankCode,
    activeTaxonCode,
    currentTaxonResponse?.code,
    isFetching,
    sections.length,
    handleValidate,
  ]);

  return (
    <TabScreenContainer>
      {!isSmallScreen && (
        <ScreenHeader
          title={t('speciesSearch.taxonomy.title')}
          actions={<SpeciesSearchBackAction />}
        />
      )}
      <YStack flex={1} gap="$true">
        <ScrollView showsVerticalScrollIndicator={false}>
          <YStack gap="$4" paddingBottom="$4">
            {selectedPath.length > 0 && (
              <XStack flexWrap="wrap" gap="$2">
                <TaxonomyBadge
                  key="taxonomy-root"
                  onPress={(): void => {
                    setSelectedPath(rootTaxonItem ? [rootTaxonItem] : []);
                  }}
                  cursor="pointer"
                  opacity={0.8}
                >
                  <SizableText size="$3">{rootBadgeLabel}</SizableText>
                </TaxonomyBadge>
                {selectedPath.map((selection, index) => {
                  const isActive = index === selectedPath.length - 1;
                  return (
                    <TaxonomyBadge
                      key={selection.id}
                      onPress={
                        isActive
                          ? undefined
                          : (): void => handleRemoveSelection(index)
                      }
                      cursor={isActive ? undefined : 'pointer'}
                      opacity={isActive ? 1 : 0.8}
                    >
                      <SizableText size="$3">{selection.label}</SizableText>
                    </TaxonomyBadge>
                  );
                })}
              </XStack>
            )}

            <YStack gap="$2">
              <SizableText size="$h6" fontFamily="$bold">
                {taxonomyRankLabel &&
                  t('speciesSearch.taxonomy.select', {
                    level: taxonomyRankLabel ?? '',
                  })}
              </SizableText>
              <Separator />
            </YStack>
            <XStack alignItems="center" gap="$1" flexWrap="wrap">
              <Ionicons
                name="information-circle-outline"
                size={18}
                color={theme.secondary50?.val}
              />
              <SizableText size="$2" color="$secondary75">
                {t('speciesSearch.taxonomy.helperPrefix')}
              </SizableText>
              <SizableText
                size="$2"
                color="$secondary75"
                textDecorationLine="underline"
                cursor="pointer"
                onPress={handleTrySearch}
              >
                {t('speciesSearch.taxonomy.helperSearch')}
              </SizableText>
              {canShowManualValidation && (
                <>
                  <SizableText size="$2" color="$secondary75">
                    {t('speciesSearch.taxonomy.helperConnector')}
                  </SizableText>
                  <SizableText
                    size="$2"
                    color="$error"
                    cursor="pointer"
                    fontFamily="$bold"
                    onPress={handleStopIdentification}
                  >
                    {t('speciesSearch.taxonomy.stopIdentification')}
                  </SizableText>
                </>
              )}
            </XStack>

            {isLoading ? (
              <YStack alignItems="center" gap="$2" paddingVertical="$4">
                <Spinner size="small" color={theme.brand?.val} />
                <SizableText size="$2" color="$secondary75">
                  {t('speciesSearch.taxonomy.loading')}
                </SizableText>
              </YStack>
            ) : sections.length > 0 ? (
              <List sections={sections} gap="$2" />
            ) : (
              <SizableText size="$2" color="$secondary75">
                {isUnsupportedLeafSelection && lastSelection?.label
                  ? t('speciesSearch.taxonomy.noResultsForSelection', {
                      label: lastSelection.label,
                    })
                  : t('speciesSearch.taxonomy.noResults')}
              </SizableText>
            )}
          </YStack>
        </ScrollView>

        {canShowManualValidation && (
          <Button
            title={t('buttons.validate')}
            variant="primary"
            onPress={handleValidate}
            width="100%"
          />
        )}
      </YStack>
    </TabScreenContainer>
  );
};
